import { createSlice } from "@reduxjs/toolkit";
import { formatDateTimeForZipMenue } from "../../utils/date";
import { format } from "date-fns";
import { getAllOwnersForInterviewThunk } from "../thunks/InterviewThunk";

export const columnVisibility = {
    SOURCED: {
        ALL: [
            { "Candidate Name": true },
            { "Email Id": true },
            { "Contact Number": true },
            { "Applied On": false },
            { "Notice Period": true },
            { "Source": false },
            { "Resume Score": true },
        ],
        MY_UPLOADS: [
            { "Candidate Name": true },
            { "Email Id": true },
            { "Contact Number": true },
            { "Applied On": false },
            { "Resume Score": true },
        ],

        SOURCED_CANDIDATES: [
            { "Candidate Name": true },
            { "Email Id": true },
            { "Contact Number": true },
            { "Notice Period": true },
            { "Source": false },
            { "Resume Score": true },
            { "Applied On": false },
        ],

        // Outreached rows come from the sourcing service's screening, not a resume
        // scan — there is no resume score or notice period on them, so those columns
        // are left out entirely rather than shown empty. "Applied On" carries the
        // date they replied.
        OUTREACHED: [
            { "Candidate Name": true },
            { "Job Role": true },
            { "Email Id": true },
            { "Contact Number": true },
            { "Applied On": true },
            { "Source": true },
        ],
    },

    INTERVIEW: {
        ALL: [
            { "Candidate Name": true },
            { "Job Role": true },
            { "Email Id": true },
            { "Contact Number": true },
            { "Invited Date": false },
            { "End Date": true },
            { "Owner": true },
            { "Interview Score": true },
            { "Status": true },
        ],
        SCHEDULED: [
            { "Candidate Name": true },
            { "Job Role": true },
            { "Email Id": true },
            { "Contact Number": true },
            { "Invited Date": false },
            { "End Date": true },
            { "Owner": true },
            { "Interview Score": true },
            { "Status": true },
        ],

        PROGRESS: [
            { "Candidate Name": true },
            { "Job Role": true },
            { "Email Id": true },
            { "Contact Number": true },
            { "Invited Date": false },
            { "End Date": true },
            { "Owner": true },
            { "Status": true },
            { "Interview Score": true },
        ],

        COMPLETED: [
            { "Candidate Name": true },
            { "Job Role": true },
            { "Email Id": true },
            { "Contact Number": true },
            { "Invited Date": false },
            { "End Date": true },
            { "Owner": true },
            { "Status": true },
            { "Interview Score": true },
        ],
    },
    DECISION: {
        ALL: [
            { "Candidate Name": true },
            { "Job Role": true },
            { "Email Id": true },
            { "Contact Number": true },
            { "Interview Date": true },
            { "Owner": true },
            { "Interview Score": true },
            { "Status": true },
        ],
        ACCEPTED: [
            { "Candidate Name": true },
            { "Job Role": true },
            { "Email Id": true },
            { "Contact Number": true },
            { "Interview Date": true },
            { "Owner": true },
            { "Interview Score": true },
            { "Status": true }
        ],
        REJECTED: [
            { "Candidate Name": true },
            { "Job Role": true },
            { "Email Id": true },
            { "Contact Number": true },
            { "Interview Date": true },
            { "Owner": true },
            { "Interview Score": true },
            { "Status": true }
        ]
    }
};


const initialState = {
    searchQuery: "",
    placeHolder: "",
    dateRange: [],
    currentInterviewPage: 1,
    itemsPerInterviewPage: 10,
    currentSource: "All",
    currentZipUpload: "All",
    currentJobRole: "",
    currentStatus: "All",
    currentOwner: "All",
    currentScoreRange: { min: 0, max: 100 },
    isColFilterOpen: false,
    allZipUploads: [],
    allSources: [],
    allJobRoles: [],
    allStatus: [],
    allOwners: [],
    showFilterDropdown: false,
    filterCount: 0,
    activeFilters: [],
    columnVisibility,
    currentJobId: null,
    scheduledStatusFilter: '',
    loading: false,
    error: null,
    applyNow: false,
}



export const filterSlice = createSlice({
    name: "filterSlice",
    initialState,
    reducers: {
        updateSearchQuery: (state, action) => {
            const query = action.payload;
            state.searchQuery = query;
        },
        toggleFilterDropdown: (state, action) => {
            state.showFilterDropdown = !state.showFilterDropdown
        },
        updateCurrentZipUpload: (state, action) => {
            const data = action.payload;

            // Handle "All"
            if (!data || data === "All") {
                state.currentZipUpload = "All";

                // remove notification filter if present
                const beforeLength = state.activeFilters.length;
                state.activeFilters = state.activeFilters.filter(
                    (filter) => filter.type !== "NOTIFICATION"
                );

                if (state.activeFilters.length !== beforeLength) {
                    state.filterCount = Math.max(0, state.filterCount - 1);
                }

                return;
            }

            const newFilter = {
                label: formatDateTimeForZipMenue(data.bulk_upload_date),
                key: data.upload_id,
                dateForValidation: data.bulk_upload_date,
                type: "NOTIFICATION",
            };

            const index = state.activeFilters.findIndex(
                (filter) => filter.type === "NOTIFICATION"
            );

            if (index !== -1) {
                // Replace existing notification filter
                state.activeFilters[index] = newFilter;
            } else {
                // Add new notification filter
                state.activeFilters.push(newFilter);
                state.filterCount += 1;
            }

            state.currentZipUpload = data;
        },

        removeActiveFilter: (state, action) => {
            const keyToRemove = action.payload;

            if (keyToRemove === "DATE_RANGE") {
                state.dateRange = []
            }

            if (keyToRemove === "JOB_ROLE") {
                state.currentJobRole = ""
            }

            if (keyToRemove === "SCORE_RANGE") {
                state.currentScoreRange = { min: 0, max: 100 };
            }

            if (keyToRemove === "SOURCE") {
                state.currentSource = "All"
            }

            if(keyToRemove === "SCHEDULED_STATUS"){
                state.scheduledStatusFilter = "All"
            }
            // Find the filter before removing
            const removedFilter = state.activeFilters.find(
                (filter) => filter.key === keyToRemove
            );

            // Remove filter
            state.activeFilters = state.activeFilters.filter(
                (filter) => filter.key !== keyToRemove
            );

            // Update filter count safely
            if (removedFilter) {
                state.filterCount = Math.max(0, state.filterCount - 1);
            }

            // If removed filter was notification, reset zip upload
            if (removedFilter?.type === "NOTIFICATION") {
                state.currentZipUpload = "All";
            }
        },

        removeAllActiveFilter: (state, action) => {
            state.activeFilters = [];
            state.filterCount = 0;
            state.currentZipUpload = "All"
            state.dateRange = []
            state.currentScoreRange = { min: 0, max: 100 };
            state.applyNow = !state.applyNow;
            state.currentOwner = "";
            state.currentSource = "All";
            state.scheduledStatusFilter = "All";
            // state.currentJobRole = "";
            state.currentJobId = null;
        },

        updateDateRange: (state, action) => {
            const [startDate, endDate] = action.payload || [];

            // Always store date range as [start, end]
            state.dateRange = [startDate || null, endDate || null];

            const filterType = "DATE_RANGE";
            // console.log({ dataaaaaaaaaaaaaaaaaaaaa: state.dateRange })
            // helper to format label
            const formatLabel = (start, end) =>
                `${format(new Date(start), "dd MMM yyyy")} - ${format(
                    new Date(end),
                    "dd MMM yyyy"
                )}`;

            const existingIndex = state.activeFilters.findIndex(
                (f) => f.type === filterType
            );

            // ✅ If both dates exist → add / replace filter
            if (startDate && endDate) {
                const newFilter = {
                    key: "DATE_RANGE",
                    type: filterType,
                    label: formatLabel(startDate, endDate),
                    dateForValidation: [startDate, endDate],
                };

                if (existingIndex !== -1) {
                    // replace existing date range filter
                    state.activeFilters[existingIndex] = newFilter;
                } else {
                    // add new date range filter
                    state.activeFilters.push(newFilter);
                    state.filterCount += 1;
                }
                return;
            }

            // ❌ If one or both dates missing → remove date range filter
            if (existingIndex !== -1) {
                state.activeFilters.splice(existingIndex, 1);
                state.filterCount = Math.max(0, state.filterCount - 1);
            }
        },

        updateAllSources: (state, action) => {
            const data = action.payload;
            if (data.length > 0) {
                state.allSources = data
            }
        },

        updateCurrentSource: (state, action) => {
            const data = action.payload;

            if (!data || !data.trim()) return;

            const filterType = "SOURCE";
            const existingIndex = state.activeFilters.findIndex(
                (f) => f.type === filterType
            );

            if (!data || !data.trim() || data === 'All') {
                state.currentSource = "All";
                if (existingIndex !== -1) {
                    state.activeFilters.splice(existingIndex, 1);
                    state.filterCount = Math.max(0, state.filterCount - 1);
                }
                return;
            }

            state.currentSource = data;

            const newFilter = {
                key: "SOURCE",
                type: filterType,
                label: data,
            };

            if (existingIndex !== -1) {
                // ✅ Replace existing SOURCE filter
                state.activeFilters[existingIndex] = newFilter;
            } else {
                // ✅ Add SOURCE filter first time
                state.activeFilters.push(newFilter);
                state.filterCount += 1;
            }
        },

        updateCurrentJobRole: (state, action) => {
            const data = action.payload;
            // if (!data.trim()) return;

            const filterType = "JOB_ROLE";
            const existingIndex = state.activeFilters.findIndex(
                (f) => f.type === filterType
            )
            if (data && data.trim()) {
                state.currentJobRole = data;
            }
            const newFilter = {
                key: "JOB_ROLE",
                type: filterType,
                label: data,
            };
            if (data === "") {
                state.currentJobRole = "";
                return;
            }
            // if (existingIndex !== -1) {
            //     // ✅ Replace existing SOURCE filter
            //     state.activeFilters[existingIndex] = newFilter;
            // } else {
            //     // ✅ Add SOURCE filter first time
            //     state.activeFilters.push(newFilter);
            //     state.filterCount += 1;
            // }
        },

        updateCurrentJobId: (state, action) => {
            state.currentJobId = action.payload;
        },

        updateCurrentOwnerRole: (state, action) => {
            const data = action.payload;
            if (!data.trim()) return;
            const filterType = "OWNER";
            const existingIndex = state.activeFilters.findIndex(
                (f) => f.type === filterType
            )
            if (data.trim()) {
                state.currentOwner = data;
            }
            const newFilter = {
                key: "OWNER",
                type: filterType,
                label: data,
            };
            if (existingIndex !== -1) {
                // ✅ Replace existing SOURCE filter
                state.activeFilters[existingIndex] = newFilter;
            } else {
                // ✅ Add SOURCE filter first time
                state.activeFilters.push(newFilter);
                state.filterCount += 1;
            }
        },

        updateCurrentInterviewPage: (state, action) => {
            const data = action.payload;
            if (data !== 0) {
                state.currentInterviewPage = data;
            }
        },
        toggleColFilter: (state, action) => {
            state.isColFilterOpen = !state.isColFilterOpen
        },
        toggleColumnVisibility: (state, action) => {
            const { tab, subTab, label } = action.payload;

            const cols = state.columnVisibility[tab][subTab];
            const col = cols.find(
                (obj) => Object.keys(obj)[0] === label
            );

            if (col) {
                col[label] = !col[label];
            }
        },
        setAllColumnsVisibility: (state, action) => {
            const { tab, subTab, value } = action.payload;

            state.columnVisibility[tab][subTab].forEach((obj) => {
                const key = Object.keys(obj)[0];
                obj[key] = value;
            });
        },
        updateCurrentScoreRange: (state, action) => {
            const { min, max } = action.payload;

            const filterType = "SCORE_RANGE";
            const existingIndex = state.activeFilters.findIndex(
                (f) => f.type === filterType
            );

            state.currentScoreRange = { min, max };

            // Only add filter if it's not the default full range
            if (min === 0 && max === 100) {
                // Remove filter if reset to default
                if (existingIndex !== -1) {
                    state.activeFilters.splice(existingIndex, 1);
                    state.filterCount = Math.max(0, state.filterCount - 1);
                }
                return;
            }

            const newFilter = {
                key: "SCORE_RANGE",
                type: filterType,
                label: `Score: ${min} - ${max}`,
            };

            if (existingIndex !== -1) {
                state.activeFilters[existingIndex] = newFilter;
            } else {
                state.activeFilters.push(newFilter);
                state.filterCount += 1;
            }
        },
        resetScoreRange: (state) => {
            const filterType = "SCORE_RANGE";
            const existingIndex = state.activeFilters.findIndex(
                (f) => f.type === filterType
            );

            state.currentScoreRange = { min: 0, max: 100 };

            if (existingIndex !== -1) {
                state.activeFilters.splice(existingIndex, 1);
                state.filterCount = Math.max(0, state.filterCount - 1);
            }
        },
        updateScheduleFilter: (state, action) => {
            const val = action.payload;
            console.log({ val })
            if (!val) {
                return;
            }
            state.scheduledStatusFilter = val;
            const filterType = "SCHEDULED_STATUS";
            const existingIndex = state.activeFilters.findIndex(
                (f) => f.type === filterType
            );

            if (val === 'All') {
                if (existingIndex !== -1) {
                    state.activeFilters.splice(existingIndex, 1);
                    state.filterCount = Math.max(0, state.filterCount - 1);
                }
                return;
            }

            const newFilter = {
                key: "SCHEDULED_STATUS",
                type: filterType,
                label: `${val}`,
            };

            if (existingIndex !== -1) {
                state.activeFilters[existingIndex] = newFilter;
            } else {
                state.activeFilters.push(newFilter);
                state.filterCount += 1;
            }
        },
        triggerApply: (state) => {
            state.applyNow = !state.applyNow;
        },
        resetApplyNow: (state) => {
            state.applyNow = false;
        },
        clearFilterSlice: () => initialState

    },
    extraReducers: (builder) => {
        builder.addCase(getAllOwnersForInterviewThunk.pending, (state, action) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getAllOwnersForInterviewThunk.fulfilled, (state, action) => {
            const data = action.payload;
            if (data) {
                state.allOwners = data;
            }
        });
        builder.addCase(getAllOwnersForInterviewThunk.rejected, (state, actions) => {
            state.loading = false;
            state.error = actions.payload
        })
    }
})


export const { updateSearchQuery, toggleFilterDropdown, updateCurrentInterviewPage, updateCurrentZipUpload, removeActiveFilter, removeAllActiveFilter, updateDateRange, updateAllSources, updateCurrentSource, updateCurrentJobRole, updateCurrentJobId, updateCurrentOwnerRole, toggleColFilter, toggleColumnVisibility, setAllColumnsVisibility, clearFilterSlice, updateCurrentScoreRange, resetScoreRange, triggerApply, resetApplyNow, updateScheduleFilter } = filterSlice.actions